// Native Google and Apple sign-in for the iOS/Android apps. The web app keeps
// using the Emergent redirect flow, which can't return to a Capacitor app, so on
// native we get an ID token from the OS sign-in sheet and the backend verifies it
// at POST /api/auth/google or POST /api/auth/apple.

import { registerPlugin } from "@capacitor/core";
import { getPlatform, isIOSApp, isNativeApp } from "./platform";

// Sign in with Apple is a small plugin inside the iOS app (ios/App/App/AppDelegate.swift).
const AppleSignIn = registerPlugin("AppleSignIn");

const IOS_CLIENT_ID = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID || "";
const WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || "";

// iOS signs in with the iOS OAuth client; Android's Credential Manager uses the web client.
export function isNativeGoogleAvailable() {
  if (!isNativeApp()) return false;
  return getPlatform() === "ios" ? !!IOS_CLIENT_ID : !!WEB_CLIENT_ID;
}

// Sign in with Apple is offered on iOS, where App Store guideline 4.8 requires it
// alongside Google. It uses the system sheet and needs no client ID.
export function isNativeAppleAvailable() {
  return isIOSApp();
}

let initPromise = null;

async function getSocialLogin() {
  const { SocialLogin } = await import("@capgo/capacitor-social-login");
  if (!initPromise) {
    const config = {};
    if (isNativeGoogleAvailable()) {
      config.google = {
        iOSClientId: IOS_CLIENT_ID || undefined,
        webClientId: WEB_CLIENT_ID || undefined,
        mode: "online",
      };
    }
    initPromise = SocialLogin.initialize(config).catch((e) => { initPromise = null; throw e; });
  }
  await initPromise;
  return SocialLogin;
}

export function isCancelError(e) {
  // Apple reports a user cancel as ASAuthorizationError 1001.
  return e?.code === "1001" || /cancel|1001/i.test(String(e?.message || e || ""));
}

// Returns a Google ID token, or throws (isCancelError tells a user cancel apart).
export async function getGoogleIdToken() {
  const SocialLogin = await getSocialLogin();
  const res = await SocialLogin.login({
    provider: "google",
    options: { scopes: ["email", "profile"] },
  });
  const idToken = res?.result?.idToken;
  if (!idToken) throw new Error("Google didn't return an ID token");
  return idToken;
}

// Returns { idToken, name }. Apple only shares the user's name on the first sign-in.
export async function getAppleIdToken() {
  const res = await AppleSignIn.authorize();
  if (!res?.identityToken) throw new Error("Apple didn't return an identity token");
  const name = [res.givenName, res.familyName].filter(Boolean).join(" ");
  return { idToken: res.identityToken, name };
}

const APP_AUTH_URL = "com.tarakundli.app://auth";

// Emergent Google sign-in for the apps when native Google client IDs aren't set:
// opens Emergent auth in an in-app browser; our /app-auth web page forwards the
// session_id back to the app via the com.tarakundli.app:// URL scheme.
export async function getEmergentSessionId() {
  const { Browser } = await import("@capacitor/browser");
  const { App } = await import("@capacitor/app");
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // The app's own origin (capacitor://localhost) can't receive the redirect, so it targets our web bridge page.
  const redirectUrl = `${process.env.REACT_APP_BACKEND_URL}/app-auth`;
  let done = false;
  let subs = [];
  const cleanup = () => { done = true; subs.forEach((s) => s.remove()); };
  return new Promise((resolve, reject) => {
    (async () => {
      subs.push(await App.addListener("appUrlOpen", ({ url }) => {
        if (done || !url?.startsWith(APP_AUTH_URL)) return;
        cleanup();
        Browser.close().catch(() => {});
        const m = url.match(/session_id=([^&]+)/);
        if (m) resolve(decodeURIComponent(m[1]));
        else reject(new Error("Sign-in didn't return a session"));
      }));
      // Android may report the browser closing just before the app link arrives.
      subs.push(await Browser.addListener("browserFinished", () => {
        setTimeout(() => { if (!done) { cleanup(); reject(new Error("cancelled")); } }, 1500);
      }));
      await Browser.open({ url: `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}` });
    })().catch((e) => { cleanup(); reject(e); });
  });
}

export async function signOutSocial() {
  if (!isNativeApp()) return;
  // Apple has no sign-out; our own session is what's cleared.
  if (!isNativeGoogleAvailable()) return;
  try {
    const SocialLogin = await getSocialLogin();
    await SocialLogin.logout({ provider: "google" }).catch(() => {});
  } catch { /* not signed in with Google */ }
}
